/**
 * ProtectedRoute - Route guard that checks permissions before rendering page content
 *
 * Shows AccessDeniedState when user lacks the required permission.
 * Shows LoadingState while permissions are being fetched.
 * The route still exists in the router — only its content is replaced (not a redirect).
 */
import { type ReactNode } from 'react';
import { AccessDeniedState, LoadingState } from '@/components/ui/page-states';
import { usePermissions } from '@/hooks/usePermissions';

interface ProtectedRouteProps {
  /** Resource to check permission for */
  resource: string;
  /** Action to check permission for */
  action: string;
  /** Page content to render when authorized */
  children: ReactNode;
  /** Custom access denied message */
  deniedMessage?: string;
}

export function ProtectedRoute({ resource, action, children, deniedMessage }: ProtectedRouteProps) {
  const { can, isLoading } = usePermissions();

  if (isLoading) {
    return <LoadingState message="Checking permissions..." />;
  }

  if (!can(resource, action)) {
    return <AccessDeniedState message={deniedMessage} backHref="/" backLabel="Go Home" />;
  }

  return <>{children}</>;
}
